import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { addDoc, collection, doc, getDocs, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { EVENTS_COLLECTION, fetchEvents } from '../utils/events';
import { QR_INDEX_COLLECTION, isLikelyQrHexId } from '../utils/qrIndex';
import './AdminEventsPage.css';

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'string') {
    const localMatch = value.trim().match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/
    );
    if (localMatch) {
      const [, day, month, year, hour, minute, second = '0'] = localMatch;
      return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return 'Sin registro';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const AdminEventsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deletingEvent, setDeletingEvent] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [eventToDelete, setEventToDelete] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ name: '', eventDate: '' });
  const [savingEvent, setSavingEvent] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      setEvents(await fetchEvents(db));
    } catch (loadError) {
      console.error('Error cargando eventos:', loadError);
      setError('No se pudieron cargar los eventos configurados.');
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const snapshot = await getDocs(collection(db, 'FreeTicket'));
      setTickets(snapshot.docs.map(ticketDoc => ({
        id: ticketDoc.id,
        ...ticketDoc.data(),
      })));
    } catch (loadError) {
      console.error('Error cargando eventos free:', loadError);
      setError('No se pudieron cargar los eventos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    loadEvents();
  }, [loadEvents, loadTickets]);

  const createEvent = async (submitEvent) => {
    submitEvent.preventDefault();
    if (!eventForm.name.trim()) {
      setMessage({ type: 'error', text: 'Ingresá un nombre de evento.' });
      return;
    }

    setSavingEvent(true);
    setMessage({ type: '', text: '' });
    try {
      await addDoc(collection(db, EVENTS_COLLECTION), {
        name: eventForm.name.trim(),
        eventDate: eventForm.eventDate,
        isActive: events.length === 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setEventForm({ name: '', eventDate: '' });
      await loadEvents();
      setMessage({ type: 'success', text: 'Evento creado. Ya podés asociarlo a entradas o activarlo para consumos.' });
    } catch (createError) {
      console.error('Error creando evento:', createError);
      setMessage({ type: 'error', text: `No se pudo crear el evento: ${createError.message}` });
    } finally {
      setSavingEvent(false);
    }
  };

  const activateEvent = async (eventId) => {
    setSavingEvent(true);
    setMessage({ type: '', text: '' });
    try {
      const batch = writeBatch(db);
      events.forEach(event => {
        batch.update(doc(db, EVENTS_COLLECTION, event.id), {
          isActive: event.id === eventId,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      await loadEvents();
      setMessage({ type: 'success', text: 'Evento activo actualizado. Los consumos nuevos se asociarán a ese evento.' });
    } catch (activateError) {
      console.error('Error activando evento:', activateError);
      setMessage({ type: 'error', text: `No se pudo activar el evento: ${activateError.message}` });
    } finally {
      setSavingEvent(false);
    }
  };

  const deactivateEvent = async (eventId) => {
    setSavingEvent(true);
    setMessage({ type: '', text: '' });
    try {
      await updateDoc(doc(db, EVENTS_COLLECTION, eventId), {
        isActive: false,
        updatedAt: serverTimestamp(),
      });
      await loadEvents();
      setMessage({ type: 'success', text: 'Evento desactivado. Los consumos nuevos quedarán sin evento activo hasta que actives otro.' });
    } catch (deactivateError) {
      console.error('Error desactivando evento:', deactivateError);
      setMessage({ type: 'error', text: `No se pudo desactivar el evento: ${deactivateError.message}` });
    } finally {
      setSavingEvent(false);
    }
  };

  const eventRows = useMemo(() => {
    const rowsByEvent = new Map();

    tickets.forEach(ticket => {
      const cartItems = Array.isArray(ticket.cartItems) ? ticket.cartItems : [];
      cartItems.forEach(item => {
        const eventKey = item.eventId || item.eventName || item.model || 'Ticket free';
        const eventName = item.eventName || item.model || 'Ticket free';
        const current = rowsByEvent.get(eventKey) || {
          eventKey,
          eventId: item.eventId || '',
          eventName,
          eventDate: item.eventDate || '',
          total: 0,
          used: 0,
          docs: new Set(),
          qrCount: 0,
          firstUsedAt: null,
          lastUsedAt: null,
        };

        const usedAt = item.isUsed ? toDate(item.usedTimestamp) : null;
        current.total += 1;
        current.qrCount += isLikelyQrHexId(item.qrValue) ? 1 : 0;
        current.docs.add(ticket.id);
        if (item.isUsed) current.used += 1;
        if (usedAt && (!current.firstUsedAt || usedAt < current.firstUsedAt)) current.firstUsedAt = usedAt;
        if (usedAt && (!current.lastUsedAt || usedAt > current.lastUsedAt)) current.lastUsedAt = usedAt;

        rowsByEvent.set(eventKey, current);
      });
    });

    const normalizedSearch = search.trim().toLowerCase();

    return Array.from(rowsByEvent.values())
      .map(row => ({
        ...row,
        pending: row.total - row.used,
        docsCount: row.docs.size,
        usagePercent: row.total ? Math.round((row.used / row.total) * 100) : 0,
      }))
      .filter(row => !normalizedSearch || row.eventName.toLowerCase().includes(normalizedSearch))
      .sort((a, b) => b.total - a.total || a.eventName.localeCompare(b.eventName, 'es'));
  }, [search, tickets]);

  const summary = useMemo(() => {
    return eventRows.reduce(
      (acc, event) => ({
        events: acc.events + 1,
        tickets: acc.tickets + event.total,
        used: acc.used + event.used,
        pending: acc.pending + event.pending,
      }),
      { events: 0, tickets: 0, used: 0, pending: 0 }
    );
  }, [eventRows]);

  const openDeleteDialog = (event) => {
    setEventToDelete(event);
    setAdminPassword('');
    setMessage({ type: '', text: '' });
  };

  const closeDeleteDialog = () => {
    if (deletingEvent) return;
    setEventToDelete(null);
    setAdminPassword('');
  };

  const confirmDeleteEvent = async (submitEvent) => {
    submitEvent.preventDefault();
    if (!eventToDelete) return;

    const currentUser = auth.currentUser;
    if (!currentUser?.email) {
      setMessage({ type: 'error', text: 'Sesión inválida. Volvé a iniciar sesión como administrador.' });
      return;
    }
    if (!adminPassword) {
      setMessage({ type: 'error', text: 'Ingresá la contraseña del administrador para eliminar el evento.' });
      return;
    }

    const eventName = eventToDelete.eventName;
    const eventKey = eventToDelete.eventKey;
    setDeletingEvent(eventName);
    setMessage({ type: '', text: '' });

    try {
      const credential = EmailAuthProvider.credential(currentUser.email, adminPassword);
      await reauthenticateWithCredential(currentUser, credential);

      let batch = writeBatch(db);
      let operations = 0;
      let deletedTickets = 0;
      let affectedDocs = 0;

      const commitBatch = async (force = false) => {
        if (operations === 0 || (!force && operations < 450)) return;
        await batch.commit();
        batch = writeBatch(db);
        operations = 0;
      };

      for (const ticket of tickets) {
        const cartItems = Array.isArray(ticket.cartItems) ? ticket.cartItems : [];
        const removedItems = cartItems.filter(item => (item.eventId || item.eventName || item.model || 'Ticket free') === eventKey);
        if (removedItems.length === 0) continue;

        const remainingItems = cartItems.filter(item => (item.eventId || item.eventName || item.model || 'Ticket free') !== eventKey);
        const ticketRef = doc(db, 'FreeTicket', ticket.id);

        if (remainingItems.length === 0) {
          batch.delete(ticketRef);
        } else {
          batch.update(ticketRef, {
            cartItems: remainingItems,
            lastUpdated: serverTimestamp(),
          });
        }

        operations += 1;
        affectedDocs += 1;
        deletedTickets += removedItems.length;
        await commitBatch();

        for (const item of removedItems) {
          if (!isLikelyQrHexId(item.qrValue)) continue;
          batch.delete(doc(db, QR_INDEX_COLLECTION, item.qrValue.toLowerCase()));
          operations += 1;
          await commitBatch();
        }
      }

      await commitBatch(true);
      await loadTickets();
      setMessage({
        type: 'success',
        text: `Evento eliminado: ${deletedTickets} entradas removidas en ${affectedDocs} documento(s).`,
      });
      setEventToDelete(null);
      setAdminPassword('');
    } catch (deleteError) {
      console.error('Error eliminando evento:', deleteError);
      const isWrongPassword = deleteError?.code === 'auth/wrong-password' || deleteError?.code === 'auth/invalid-credential';
      setMessage({
        type: 'error',
        text: isWrongPassword
          ? 'Contraseña incorrecta. No se eliminó ningún evento.'
          : `No se pudo eliminar el evento: ${deleteError.message}`,
      });
    } finally {
      setDeletingEvent('');
    }
  };

  return (
    <div className="admin-events-page">
      <header className="admin-events-page__hero">
        <div>
          <span className="admin-events-page__eyebrow">Administración global</span>
          <h1>Eventos y entradas free</h1>
          <p>Configurá el evento activo, asociá entradas y consumos, y revisá el uso de tickets free.</p>
        </div>
        <div className="admin-events-page__actions">
          <button type="button" onClick={loadTickets} disabled={loading}>
            {loading ? 'Actualizando...' : 'Actualizar'}
          </button>
          <Link to="/admin">Volver al panel</Link>
        </div>
      </header>

      <section className="admin-events-page__stats" aria-label="Resumen de eventos">
        <article>
          <span>Eventos</span>
          <strong>{events.length}</strong>
        </article>
        <article>
          <span>Entradas</span>
          <strong>{summary.tickets}</strong>
        </article>
        <article>
          <span>Usadas</span>
          <strong>{summary.used}</strong>
        </article>
        <article>
          <span>Pendientes</span>
          <strong>{summary.pending}</strong>
        </article>
      </section>

      <section className="admin-events-page__panel admin-events-page__panel--events">
        <div className="admin-events-page__panel-head">
          <div>
            <h2>Eventos configurados</h2>
            <p>El evento activo se usa para asociar consumos nuevos de `otherProducts`.</p>
          </div>
          <form className="admin-events-page__event-form" onSubmit={createEvent}>
            <input
              type="text"
              value={eventForm.name}
              onChange={event => setEventForm(prev => ({ ...prev, name: event.target.value }))}
              placeholder="Nombre del evento"
            />
            <input
              type="date"
              value={eventForm.eventDate}
              onChange={event => setEventForm(prev => ({ ...prev, eventDate: event.target.value }))}
            />
            <button type="submit" disabled={savingEvent}>
              {savingEvent ? 'Guardando...' : 'Crear evento'}
            </button>
          </form>
        </div>

        <div className="admin-events-page__configured-list">
          {events.length === 0 ? (
            <div className="admin-events-page__state">Todavía no hay eventos configurados.</div>
          ) : events.map(event => (
            <article key={event.id} className={`admin-events-page__configured-event ${event.isActive ? 'is-active' : ''}`}>
              <div>
                <strong>{event.name}</strong>
                <span>{event.eventDate || 'Sin fecha'}{event.isActive ? ' · Activo' : ''}</span>
              </div>
              <div className="admin-events-page__configured-actions">
                <button type="button" onClick={() => activateEvent(event.id)} disabled={savingEvent || event.isActive}>
                  {event.isActive ? 'Activo' : 'Activar'}
                </button>
                {event.isActive && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => deactivateEvent(event.id)}
                    disabled={savingEvent}
                  >
                    Desactivar
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-events-page__panel">
        <div className="admin-events-page__panel-head">
          <div>
            <h2>Listado de eventos</h2>
            <p>Eliminar un registro borra sus entradas free y los índices QR asociados.</p>
          </div>
          <label>
            Buscar evento
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Nombre del evento"
            />
          </label>
        </div>

        {message.text && (
          <div className={`admin-events-page__message ${message.type}`}>
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="admin-events-page__state">Cargando eventos...</div>
        ) : error ? (
          <div className="admin-events-page__state error">{error}</div>
        ) : eventRows.length === 0 ? (
          <div className="admin-events-page__state">No hay eventos para mostrar.</div>
        ) : (
          <div className="admin-events-page__list">
            {eventRows.map(event => (
              <article className="admin-events-page__event" key={event.eventName}>
                <div className="admin-events-page__event-main">
                  <strong>{event.eventName}</strong>
                  <span>
                    {event.total} entradas · {event.used} usadas · {event.pending} pendientes · {event.docsCount} documento(s)
                  </span>
                  <div className="admin-events-page__progress" aria-label={`${event.usagePercent}% usado`}>
                    <span style={{ width: `${event.usagePercent}%` }} />
                  </div>
                </div>
                <div className="admin-events-page__event-meta">
                  <span>Primer uso: {formatDate(event.firstUsedAt)}</span>
                  <span>Último uso: {formatDate(event.lastUsedAt)}</span>
                  <span>QR indexados: {event.qrCount}</span>
                </div>
                <button
                  type="button"
                  className="admin-events-page__delete"
                  onClick={() => openDeleteDialog(event)}
                  disabled={Boolean(deletingEvent)}
                >
                  {deletingEvent === event.eventName ? 'Eliminando...' : 'Eliminar registro'}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {eventToDelete && (
        <div className="admin-events-page__modal-backdrop" role="presentation" onClick={closeDeleteDialog}>
          <form className="admin-events-page__modal" onSubmit={confirmDeleteEvent} onClick={event => event.stopPropagation()}>
            <span className="admin-events-page__modal-eyebrow">Confirmación requerida</span>
            <h2>Eliminar registro</h2>
            <p>
              Para eliminar <strong>{eventToDelete.eventName}</strong>, ingresá la contraseña del administrador actual.
            </p>
            <div className="admin-events-page__modal-summary">
              {eventToDelete.total} entradas · {eventToDelete.used} usadas · {eventToDelete.pending} pendientes
            </div>
            <label>
              Contraseña del admin
              <input
                type="password"
                value={adminPassword}
                onChange={event => setAdminPassword(event.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </label>
            <div className="admin-events-page__modal-actions">
              <button type="button" onClick={closeDeleteDialog} disabled={Boolean(deletingEvent)}>
                Cancelar
              </button>
              <button type="submit" className="danger" disabled={Boolean(deletingEvent)}>
                {deletingEvent ? 'Eliminando...' : 'Eliminar registro'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminEventsPage;
